import { CaretLeftIcon } from "@foreday/ui/components/icons";
import { cn } from "@foreday/ui/lib/utils";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  isRedirect,
  Link,
  redirect,
} from "@tanstack/react-router";
import type * as React from "react";
import { companyLogo } from "@/client/components/company-switcher";
import { DocumentSkeleton } from "@/client/components/document-skeleton";
import {
  HeldButton,
  HeldMenu,
  HeldTitle,
} from "@/client/components/page-pending";
import { PanelLead } from "@/client/components/panel-lead";
import { ScreenHeading } from "@/client/components/screen-heading";
import { api, drain, isRefusal } from "@/client/lib/api";
import { clientBillingQuery, clientsQuery } from "@/client/lib/clients-query";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { ensureTenantData, requireModule } from "@/client/lib/session";
import { useNow } from "@/client/lib/use-now";
import { dayIn } from "@/features/attendance/rules";
import {
  DRAFT_TITLE,
  DraftEditor,
  DraftFormSkeleton,
  DraftLayout,
} from "@/features/invoices/components/draft-editor";
import {
  InvoiceIssued,
  IssuedBelowPending,
  IssuedLayout,
  primaryActionOf,
} from "@/features/invoices/components/invoice-issued";
import { invoicesCopy } from "@/features/invoices/copy";
import {
  draftEdited,
  ensureInvoice,
  ensureIssuedInvoice,
  type InvoiceDetail,
  invoiceQuery,
  invoiceSettingsQuery,
  invoicesKey,
  newDrafts,
  numberingQuery,
} from "@/features/invoices/invoice-query";
import {
  type InvoiceListRow,
  invoicesQuery,
} from "@/features/invoices/invoices-query";
import { invoiceSearch } from "@/features/invoices/list-search";

/** The invoice date of an undated draft turns over at the company's midnight. */
const TICK = 60_000;

/** A new draft starts with the company's default note, so only a different one is a change. */
const untouched = (
  draft: InvoiceDetail | undefined,
  startedWith: string | null,
) =>
  draft?.status === "draft" &&
  !draft.clientId &&
  draft.lines.length === 0 &&
  !draft.reference &&
  draft.note === startedWith;

export const Route = createFileRoute(
  "/_authenticated/$slug/invoices/$invoiceId",
)({
  validateSearch: invoiceSearch,
  // An Admin's and a Manager's; the server refuses an Employee too.
  beforeLoad: ({ context }) => {
    requireModule(context, "invoices");
    if (context.role === "employee") {
      throw redirect({ to: "/$slug", params: { slug: context.companySlug } });
    }
  },
  loader: async ({ context, params }) => {
    const [found] = await Promise.all([
      ensureInvoice(context.queryClient, params.invoiceId),
      ensureTenantData(context.queryClient, clientsQuery),
      ensureTenantData(context.queryClient, invoiceSettingsQuery),
      // The next number is shown only where an Admin finalises or sends.
      context.role === "admin" &&
        ensureTenantData(context.queryClient, numberingQuery),
    ]);
    const invoice = found
      ? context.queryClient.getQueryData(
          invoiceQuery(params.invoiceId).queryKey,
        )
      : undefined;
    if (invoice && invoice.status !== "draft") {
      await ensureIssuedInvoice(context.queryClient, params.invoiceId);
    } else if (invoice?.clientId) {
      // The bill-to block draws with the draft. A failed read leaves the editor to read it again
      // and draw without it meanwhile, rather than failing the whole draft.
      await ensureTenantData(
        context.queryClient,
        clientBillingQuery(invoice.clientId, invoice.id),
      ).catch((failure: unknown) => {
        if (isRedirect(failure)) throw failure;
      });
    }
    return { found };
  },
  // New invoice makes the draft at once, so one left untouched on that first visit was never
  // started and leaves no empty row behind. One opened again may have been edited on an earlier
  // visit, and one saved to here is kept: a save may be in flight, or an Undo still open.
  onLeave: ({ context: { queryClient }, params: { invoiceId } }) => {
    if (!newDrafts.has(invoiceId)) return;
    const startedWith = newDrafts.get(invoiceId) ?? null;
    newDrafts.delete(invoiceId);
    const held = queryClient.getQueryData(invoiceQuery(invoiceId).queryKey);
    if (!untouched(held, startedWith) || draftEdited(queryClient, invoiceId))
      return;
    // Out of the list before it draws. A route callback cannot use `useOptimisticMutation`, a hook,
    // so the row is put back by hand when the draft stays.
    const row = queryClient
      .getQueryData<InvoiceListRow[]>(invoicesKey)
      ?.find((each) => each.id === invoiceId);
    queryClient.setQueryData<InvoiceListRow[]>(invoicesKey, (rows) =>
      rows?.filter((each) => each.id !== invoiceId),
    );
    const putBack = () =>
      queryClient.setQueryData<InvoiceListRow[]>(invoicesKey, (rows) =>
        rows && row && !rows.some((each) => each.id === invoiceId)
          ? [...rows, row]
          : rows,
      );
    // Decided on the server's copy: someone else may have filled it in since this tab read it.
    queryClient
      .fetchQuery({ ...invoiceQuery(invoiceId), staleTime: 0 })
      .then(async (fresh) => {
        if (!untouched(fresh, startedWith)) return putBack();
        await drain(api.invoices[":id"].$delete({ param: { id: invoiceId } }));
        queryClient.removeQueries({
          queryKey: invoiceQuery(invoiceId).queryKey,
        });
      })
      // A refused or failed delete keeps the draft, as before; only a fault is worth a report.
      .catch((failure) => {
        putBack();
        if (!isRefusal(failure)) reportError(failure);
      });
  },
  // Invoice to invoice (Duplicate, or Open invoice in the Foreday panel) stays on this route, so a
  // new draft left that way loses its mark here rather than in `onLeave`.
  onStay: ({ params: { invoiceId } }) => {
    for (const id of newDrafts.keys()) {
      if (id !== invoiceId) newDrafts.delete(id);
    }
  },
  head: async ({ match }) => {
    const { invoicesCopy } = await import("@/features/invoices/copy");
    return {
      meta: [
        {
          title: pageTitle(invoicesCopy.list.title, match.context.companyName),
        },
      ],
    };
  },
  pendingMs: PENDING_MS,
  pendingComponent: InvoicePending,
  component: Invoice,
});

function Back({ inColumn = false }: { readonly inColumn?: boolean }) {
  const { slug } = Route.useParams();
  return (
    <PanelLead>
      {/* On a phone the way back stands above the screen, so it takes the screen's side padding,
          which a page's column already has. */}
      <Link
        to="/$slug/invoices"
        params={{ slug }}
        className={cn(
          "-mx-2 inline-flex min-h-[var(--size-touch)] items-center gap-1.5 self-start rounded-full px-2 font-semibold text-body text-muted-foreground outline-none transition-colors duration-[var(--duration-fast)] ease-ui hover:text-foreground focus-visible:focus-ring",
          !inColumn && "max-md:mx-2",
        )}
      >
        <CaretLeftIcon aria-hidden="true" className="size-[var(--icon-size)]" />
        {invoicesCopy.editor.back}
      </Link>
    </PanelLead>
  );
}

/**
 * A draft's landmark, around the way back too: on a phone it stands here rather than in the top row.
 * An issued invoice is a Document page, which draws its own.
 */
function InvoiceFrame({
  busy,
  children,
}: {
  readonly busy?: boolean;
  readonly children: React.ReactNode;
}) {
  return (
    // The page's own top and gap, so a draft's title sits where the issued invoice's does.
    <main
      aria-busy={busy || undefined}
      className="flex flex-1 flex-col gap-6 pt-[var(--pad-panel-top)]"
    >
      <Back />
      {children}
    </main>
  );
}

/**
 * The invoice's first load, laid out as the draft or the issued invoice draws it; the working set's
 * list row says which, and the client's name, and an invoice it does not hold is taken as issued.
 * Its actions wait on the invoice.
 */
function InvoicePending() {
  const { invoiceId } = Route.useParams();
  const { role } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const row =
    queryClient.getQueryData(invoiceQuery(invoiceId).queryKey) ??
    queryClient
      .getQueryData(invoicesQuery.queryKey)
      ?.find((one) => one.id === invoiceId);
  const name = queryClient
    .getQueryData(clientsQuery.queryKey)
    ?.find((client) => client.id === row?.clientId)?.name;
  // An invoice not held is most often one sent and waiting on payment.
  const state = row ?? { status: "issued", sentAt: "sent" };
  const primary = primaryActionOf(state, role === "admin");
  const title = (className: string) =>
    name === undefined ? (
      <HeldTitle className={className} />
    ) : (
      <ScreenHeading className={className}>{name}</ScreenHeading>
    );
  if (row?.status !== "draft") {
    return (
      <IssuedLayout
        busy
        back={<Back inColumn />}
        title={title("text-heading")}
        standing={
          <>
            <HeldTitle className="text-body" lines={["w-40"]} />
            <HeldTitle className="text-body" lines={["w-56"]} />
          </>
        }
        actions={
          <>
            {primary ? <HeldButton>{primary}</HeldButton> : null}
            <HeldMenu />
          </>
        }
        preview={<DocumentSkeleton />}
        below={<IssuedBelowPending invoice={state} />}
      />
    );
  }
  return (
    <InvoiceFrame busy>
      <DraftLayout
        title={
          <ScreenHeading className={DRAFT_TITLE}>
            {invoicesCopy.editor.title}
          </ScreenHeading>
        }
        actions={
          <>
            <HeldButton className="@panel-xl/main:hidden">
              {invoicesCopy.editor.preview}
            </HeldButton>
            <HeldMenu />
            {role === "admin" ? (
              <HeldButton>{invoicesCopy.editor.send}</HeldButton>
            ) : null}
          </>
        }
        form={<DraftFormSkeleton />}
        preview={<DocumentSkeleton />}
      />
    </InvoiceFrame>
  );
}

function Invoice() {
  const { found } = Route.useLoaderData();
  if (found) return <Found />;
  return (
    <InvoiceFrame>
      <div className="px-4 md:px-[var(--pad-panel-x)]">
        <ScreenHeading className="text-heading">
          {invoicesCopy.refused.notFound}
        </ScreenHeading>
      </div>
    </InvoiceFrame>
  );
}

function Found() {
  const { slug, invoiceId } = Route.useParams();
  const { companyTimezone, companyName, companyLogoKey, name, role } =
    Route.useRouteContext();
  const now = useNow(TICK);
  const { data } = useSuspenseQuery(invoiceQuery(invoiceId));
  const today = dayIn(companyTimezone, now);
  if (data.status !== "draft") {
    return (
      <InvoiceIssued invoice={data} today={today} back={<Back inColumn />} />
    );
  }
  return (
    <InvoiceFrame>
      <DraftEditor
        slug={slug}
        invoice={data}
        today={today}
        timeZone={companyTimezone}
        sender={name ?? ""}
        companyName={companyName ?? ""}
        companyLogo={companyLogo(companyLogoKey)}
        admin={role === "admin"}
      />
    </InvoiceFrame>
  );
}
